import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Send, HelpCircle, ChevronRight, Mail, Clock, MessageSquare, Paperclip } from 'lucide-react';
import { openChat } from '../../redux/AIChatSlice/aiChatSlice';
import Navbaar from '../../components/Navbar/Navbaar';
import Sidebar from '../../components/Navbar/SideNavbar';
import api from '../../api/axios';

const TOPICS = [
    'Issue with hiring signals not loading',
    'Job matching / scoring question',
    'Account or billing issue',
    'LinkedIn integration problem',
    'AI Assistant not responding',
    'Feature request',
    'Other',
];

const HELP_ARTICLES = [
    {
        q: 'How does match scoring work?',
        a: 'Our AI compares your profile — skills, experience, location, and title — against the job requirements and generates a match score from 0–100. A higher score means stronger alignment. Open any job card to see the top factors driving your score.',
    },
    {
        q: 'Set up your weekly cadence',
        a: 'Your weekly cadence sets how many applications, networking outreaches, and follow-ups you aim to complete each week. Go to Daily Tasks and adjust your targets. Career Champ will then generate personalised daily action items to keep you on track.',
    },
    {
        q: 'Connect LinkedIn for warmer intros',
        a: 'Go to Profile → Integrations and connect your LinkedIn account. Once linked, Career Champ identifies mutual connections at companies you\'re targeting so your outreach arrives as a warm intro rather than a cold message.',
    },
    {
        q: 'Manage credits and Plays',
        a: 'Credits power AI-assisted actions called Plays — drafting cover letters, sending connection requests, requesting referrals, and more. Each Play costs a set number of credits. Click "Buy Credits" in the top bar to top up anytime, or view your usage history from the Credits widget.',
    },
    {
        q: 'Cancel or pause subscription',
        a: 'Open Subscription in the left sidebar and choose Manage Subscription. You can pause or cancel at any time — changes take effect at the end of your current billing cycle and your data is preserved if you return.',
    },
];

const Sk = ({ className = '' }) => (
    <div className={`animate-pulse bg-[#E9EBF0] rounded-lg ${className}`} />
);

const Support = () => {
    const { user } = useSelector(state => state.auth);
    const dispatch = useDispatch();

    const [ready, setReady] = useState(false);
    const [topic, setTopic] = useState(TOPICS[0]);
    const [message, setMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [ticketId, setTicketId] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const [openArticle, setOpenArticle] = useState(null);

    useEffect(() => { const t = setTimeout(() => setReady(true), 400); return () => clearTimeout(t); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim() || submitting) return;
        setSubmitting(true);
        setErrorMsg('');
        try {
            const formData = new FormData();
            formData.append('topic', topic);
            formData.append('message', message.trim());
            if (attachment) formData.append('attachment', attachment);

            const res = await api.post('/api/support', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setTicketId(res.data.ticketId || '');
            setSubmitted(true);
            setMessage('');
            setAttachment(null);
            setTimeout(() => setSubmitted(false), 6000);
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || 'Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleFileDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
        if (file) setAttachment(file);
    };

    if (!ready) return (
        <div className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col">
            <div className="flex w-full"><Navbaar /></div>
            <div className="flex flex-row flex-1 min-h-0">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0"><Sidebar /></div>
                <div className="flex flex-1 overflow-y-auto w-full p-6">
                    <div className="w-full">
                        <div className="grid gap-4" style={{ gridTemplateColumns: '1.5fr 1fr', alignItems: 'start' }}>
                            {/* left: form skeleton */}
                            <div className="bg-white border border-[#E6E8EE] rounded-2xl p-6 flex flex-col gap-4">
                                <Sk className="h-5 w-56 mb-1" />
                                <Sk className="h-3.5 w-72 mb-2" />
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex flex-col gap-1.5"><Sk className="h-3 w-20" /><Sk className="h-10 w-full rounded-xl" /></div>
                                    <div className="flex flex-col gap-1.5"><Sk className="h-3 w-12" /><Sk className="h-10 w-full rounded-xl" /></div>
                                </div>
                                <div className="flex flex-col gap-1.5"><Sk className="h-3 w-14" /><Sk className="h-10 w-full rounded-xl" /></div>
                                <div className="flex flex-col gap-1.5"><Sk className="h-3 w-20" /><Sk className="h-36 w-full rounded-xl" /></div>
                                <Sk className="h-14 w-full rounded-xl" />
                                <div className="flex justify-end gap-2.5">
                                    <Sk className="h-9 w-20 rounded-xl" /><Sk className="h-9 w-24 rounded-xl" />
                                </div>
                            </div>
                            {/* right: cards skeleton */}
                            <div className="flex flex-col gap-4">
                                <div className="bg-white border border-[#E6E8EE] rounded-2xl p-5 flex flex-col gap-3">
                                    <Sk className="h-4 w-40 mb-1" />
                                    {[1,2,3,4,5].map(i => <Sk key={i} className="h-3.5 w-full" />)}
                                </div>
                                <Sk className="h-36 w-full rounded-2xl" />
                                <div className="bg-white border border-[#E6E8EE] rounded-2xl p-5 flex flex-col gap-3">
                                    <Sk className="h-4 w-32 mb-1" />
                                    <Sk className="h-3.5 w-48" /><Sk className="h-3.5 w-40" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col">
            <div className="flex w-full">
                <Navbaar />
            </div>
            <div className="flex flex-row flex-1 min-h-0 transition-all duration-300">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                <div className="flex flex-1 overflow-y-auto w-full p-6">
                    <div className="w-full flex flex-col gap-4 text-left">

                        {submitted && (
                            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800 font-medium font-dmsans">
                                <span>✅</span>
                                <span>Ticket {ticketId && <strong>#{ticketId}</strong>} submitted — we'll respond within 24 hours.</span>
                            </div>
                        )}
                        {errorMsg && (
                            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 font-medium font-dmsans">
                                <span>⚠️</span> {errorMsg}
                            </div>
                        )}

                        <div className="grid gap-4" style={{ gridTemplateColumns: '1.5fr 1fr', alignItems: 'start' }}>

                            {/* ── Left: contact form ── */}
                            <div className="bg-white border border-[#E6E8EE] rounded-2xl p-6">
                                <h3 className="text-base font-semibold text-gray-900 mb-1">For any other queries, write to us</h3>
                                <p className="text-sm text-gray-500 mb-5">Our team will get back to you within 24 hours.</p>
                                <div className="h-px bg-[#E6E8EE] mb-5" />

                                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                                    {/* Name + Email row */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Your name</label>
                                            <div className="px-3 py-2.5 border border-[#E6E8EE] rounded-xl text-sm text-gray-900 bg-white">
                                                {user?.name || user?.email?.split('@')[0] || '—'}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email</label>
                                            <div className="px-3 py-2.5 border border-[#E6E8EE] rounded-xl text-sm text-gray-900 bg-white truncate">
                                                {user?.email || 'your@email.com'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Topic */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Topic</label>
                                        <div className="relative">
                                            <select
                                                value={topic}
                                                onChange={e => setTopic(e.target.value)}
                                                className="w-full px-3 py-2.5 border border-[#E6E8EE] rounded-xl text-sm text-gray-900 bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-[#3F57E033] focus:border-[#3F57E0] transition-all pr-8"
                                            >
                                                {TOPICS.map(t => <option key={t}>{t}</option>)}
                                            </select>
                                            <ChevronRight size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 rotate-90 pointer-events-none" />
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Message</label>
                                        <textarea
                                            value={message}
                                            onChange={e => setMessage(e.target.value)}
                                            placeholder="Describe your issue in as much detail as possible…"
                                            rows={7}
                                            required
                                            className="w-full px-3.5 py-3 border border-[#E6E8EE] rounded-xl text-sm text-gray-700 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-[#3F57E033] focus:border-[#3F57E0] transition-all leading-relaxed"
                                        />
                                    </div>

                                    {/* Attachment drop zone */}
                                    <div
                                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={handleFileDrop}
                                        className="border border-dashed rounded-xl px-4 py-3 flex items-center justify-between transition-colors"
                                        style={{
                                            borderColor: dragOver ? '#3F57E0' : '#E6E8EE',
                                            background: dragOver ? '#EEF1FE' : '#FAFAFB',
                                        }}
                                    >
                                        <div className="flex items-center gap-3 text-sm text-gray-500">
                                            <span className="w-8 h-8 rounded-lg bg-white border border-[#E6E8EE] flex items-center justify-center flex-shrink-0">
                                                <Paperclip size={14} className="text-gray-600" />
                                            </span>
                                            {attachment ? (
                                                <span className="text-gray-800 font-medium">{attachment.name}</span>
                                            ) : (
                                                <span>
                                                    Drag a screenshot or{' '}
                                                    <label className="cursor-pointer font-medium" style={{ color: '#3F57E0' }}>
                                                        browse files
                                                        <input type="file" className="hidden" accept="image/*,video/*" onChange={handleFileDrop} />
                                                    </label>
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-400 flex-shrink-0">PNG, JPG, MP4 · max 10 MB</span>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex justify-end gap-2.5 mt-1">
                                        <button
                                            type="button"
                                            onClick={() => { setMessage(''); setAttachment(null); }}
                                            className="px-4 py-2 text-sm font-medium text-gray-600 border border-[#E6E8EE] rounded-xl bg-white hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={submitting || !message.trim()}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
                                            style={{ backgroundColor: '#3F57E0' }}
                                        >
                                            <Send size={14} />
                                            {submitting ? 'Sending…' : 'Submit'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* ── Right column ── */}
                            <div className="flex flex-col gap-4">

                                {/* Help Center */}
                                <div className="bg-white border border-[#E6E8EE] rounded-2xl p-5">
                                    <div className="flex items-center gap-2.5 mb-1">
                                        <span className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ background: '#EEF1FE' }}>
                                            <HelpCircle size={16} style={{ color: '#3F57E0' }} />
                                        </span>
                                        <h3 className="text-sm font-semibold text-gray-900">Browse the Help Center</h3>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-4 text-left">Most common questions are already answered.</p>
                                    <div className="flex flex-col">
                                        {HELP_ARTICLES.map((item, i) => {
                                            const isOpen = openArticle === i;
                                            return (
                                                <div key={i} className="border-b border-[#E6E8EE] last:border-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => setOpenArticle(isOpen ? null : i)}
                                                        className="w-full flex items-center justify-between py-2.5 text-sm text-left text-gray-800 hover:text-[#3F57E0] transition-colors group"
                                                    >
                                                        <span className={isOpen ? 'text-[#3F57E0] font-medium' : ''}>{item.q}</span>
                                                        <ChevronRight
                                                            size={14}
                                                            className={`flex-shrink-0 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-90 text-[#3F57E0]' : 'text-gray-400 group-hover:text-[#3F57E0]'}`}
                                                        />
                                                    </button>
                                                    {isOpen && (
                                                        <p className="text-xs text-gray-500 leading-relaxed pb-3 text-left">
                                                            {item.a}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Live chat */}
                                <div className="rounded-2xl p-5 text-white"
                                    style={{ background: 'linear-gradient(135deg, #1F2A56, #3F57E0)' }}>
                                    <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3"
                                        style={{ background: 'rgba(255,255,255,.18)' }}>
                                        <MessageSquare size={18} />
                                    </div>
                                    <h3 className="text-sm font-semibold mb-1.5">Chat with our AI assistant</h3>
                                    <p className="text-xs mb-4" style={{ opacity: .85 }}>
                                        Get instant answers about jobs, networking, your profile, and more — available 24/7.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => dispatch(openChat())}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white text-sm font-semibold rounded-xl hover:bg-gray-100 transition-colors"
                                        style={{ color: '#1F2A56' }}>
                                        Start a chat
                                        <ChevronRight size={14} />
                                    </button>
                                </div>

                                {/* Contact directly */}
                                <div className="bg-white border border-[#E6E8EE] rounded-2xl p-5">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Contact directly</h3>
                                    <div className="flex flex-col gap-2.5">
                                        <div className="flex items-center gap-2.5 text-sm text-gray-700">
                                            <Mail size={14} className="text-gray-400 flex-shrink-0" />
                                            <span>info@careerchamp.io</span>
                                        </div>
                                        <div className="flex items-center gap-2.5 text-sm text-gray-700">
                                            <Clock size={14} className="text-gray-400 flex-shrink-0" />
                                            <span>Avg. first reply <strong>under 6 hours</strong></span>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Support;
